import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "../lib/toast";

export function buildGoogleAuthUrl(googleClientId, origin) {
  const redirectUri = `${origin}/auth/google/callback`;
  const authUrl = new URL("https://accounts.google.com/o/oauth2/v2/auth");
  authUrl.searchParams.set("client_id", googleClientId);
  authUrl.searchParams.set("redirect_uri", redirectUri);
  authUrl.searchParams.set("response_type", "code");
  authUrl.searchParams.set("scope", "openid email profile");
  authUrl.searchParams.set("access_type", "online");
  authUrl.searchParams.set("prompt", "select_account");
  return authUrl.toString();
}

export function buildFacebookAuthUrl(facebookAppId, origin) {
  const redirectUri = `${origin}/auth/facebook/callback`;
  const authUrl = new URL("https://www.facebook.com/dialog/oauth");
  authUrl.searchParams.set("client_id", facebookAppId);
  authUrl.searchParams.set("redirect_uri", redirectUri);
  authUrl.searchParams.set("response_type", "code");
  authUrl.searchParams.set("scope", "email,public_profile");
  return authUrl.toString();
}

function Login() {
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const handleGoogleLogin = () => {
    if (!googleClientId) {
      toast.error("Google login is not configured yet.");
      return;
    }

    window.location.assign(buildGoogleAuthUrl(googleClientId, window.location.origin));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/recipes");
    } catch (err) {
      console.error(err);
      setErrorMsg("Invalid email or password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#eef4ff] text-slate-900">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(24,119,242,0.2),_transparent_35%),radial-gradient(circle_at_80%_20%,_rgba(56,189,248,0.18),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(16,185,129,0.14),_transparent_30%)]" />
        <div className="absolute inset-0 opacity-50 [background-image:linear-gradient(rgba(255,255,255,0.55)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.55)_1px,transparent_1px)] [background-size:40px_40px]" />
        <div className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-blue-300/30 blur-3xl" />
        <div className="absolute right-[-5rem] top-[-3rem] h-96 w-96 rounded-full bg-sky-200/40 blur-3xl" />
        <div className="absolute bottom-[-6rem] left-1/3 h-80 w-80 rounded-full bg-emerald-200/30 blur-3xl" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col justify-center px-6 py-10 lg:flex-row lg:items-center lg:gap-16">
        <section className="mx-auto max-w-xl flex-1 text-center lg:mx-0 lg:text-left">
          <div className="type-eyebrow inline-flex items-center rounded-full border border-blue-100 bg-white px-3 py-1 text-blue-700 shadow-sm dark:text-blue-700">
            CartNom
          </div>
          <h1 className="type-page-title mt-6 text-[#1877f2] sm:text-5xl">
            Cook, plan, and shop without losing the thread.
          </h1>
          <p className="type-body mt-5 text-slate-600 sm:text-xl">
            Turn saved recipes into a real dinner plan, a smarter pantry, and a grocery list that stays in sync.
          </p>
        </section>

        <section className="mx-auto mt-10 w-full max-w-md lg:mt-0">
          <div className="rounded-3xl border border-white/80 bg-white/92 p-6 shadow-[0_30px_80px_-30px_rgba(24,119,242,0.35)] backdrop-blur sm:p-8">
            <h2 className="type-section-title text-center">Log in to CartNom</h2>
            <p className="type-muted mt-2 text-center">
              Pick up right where your next meal plan left off.
            </p>

            {errorMsg && (
              <p className="mt-5 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-center text-sm text-red-600">
                {errorMsg}
              </p>
            )}

            <form onSubmit={handleLogin} className="mt-6 space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-[#1877f2] focus:bg-white focus:ring-4 focus:ring-blue-100"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                    Password
                  </label>
                  <Link to="/forgot-password" className="text-xs font-semibold text-[#1877f2] hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-[#1877f2] focus:bg-white focus:ring-4 focus:ring-blue-100"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-500">
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                  className="rounded border-slate-300 text-[#1877f2] focus:ring-[#1877f2]"
                />
                Show password
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-2xl bg-[#1877f2] py-3 text-sm font-semibold text-white transition hover:bg-[#166fe5] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Logging In..." : "Log In"}
              </button>
            </form>

            <div className="my-6 h-px bg-slate-200" />

            <button
              type="button"
              onClick={handleGoogleLogin}
              className="flex w-full items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#EA4335" d="M9 7.364v3.49h4.85c-.213 1.123-.853 2.074-1.82 2.713l2.945 2.286C16.692 14.27 17.727 11.95 17.727 9c0-.56-.05-1.098-.145-1.636H9Z"/>
                <path fill="#34A853" d="M9 18c2.455 0 4.514-.814 6.018-2.214l-2.945-2.286c-.815.546-1.857.868-3.073.868-2.364 0-4.366-1.596-5.082-3.741l-3.045 2.35C2.364 15.95 5.445 18 9 18Z"/>
                <path fill="#4A90E2" d="M3.918 10.627A5.41 5.41 0 0 1 3.634 9c0-.566.098-1.118.284-1.627L.873 5.023A8.986 8.986 0 0 0 0 9c0 1.45.346 2.823.873 3.977l3.045-2.35Z"/>
                <path fill="#FBBC05" d="M9 3.632c1.336 0 2.536.459 3.482 1.359l2.614-2.614C13.51.891 11.455 0 9 0 5.445 0 2.364 2.05.873 5.023l3.045 2.35C4.634 5.228 6.636 3.632 9 3.632Z"/>
              </svg>
              Continue with Google
            </button>

            <p className="mt-4 text-center text-sm text-slate-500">
              New to CartNom?{" "}
              <Link to="/register" className="font-semibold text-[#1877f2] hover:underline">
                Register here
              </Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Login;
